import classnames from "classnames";
import type { ReactNode } from "react";
import { Box } from "../Box/Box";
import type { Color } from "../utils/colors";
import type { ResponsiveProp } from "../utils/responsiveProp";
import styles from "./Text.module.css";

export type TextSize =
  | "xsmall"
  | "small"
  | "standard"
  | "large"
  | "xlarge"
  | "xxlarge"
  | "xxxlarge";
export interface Props {
  size?: TextSize;
  weight?: "regular" | "medium" | "semi-strong" | "strong";
  transform?: "uppercase" | "capitalize";
  align?: ResponsiveProp<"left" | "right" | "center">;
  truncate?: boolean;
  clamped?: "1" | "2" | "3" | "4" | "5";
  /** Text color - note "primary" is not always safe to use over a white background for text */
  color?: Color;
  lineThrough?: boolean;
  children: ReactNode;
  /** If a font tag is not provided it will default to inherit the font-family */
  font?: "accent" | "regular" | "special";
  /** Truncate priority if set to low it will attempt to truncate first*/
  priority?: "low";
  lineHeight?: "standard" | "large";
  whiteSpace?: "pre-wrap" | "pre-line" | "nowrap";
  wordWrap?: "break-word";
  inline?: boolean;
  visibility?: "visible" | "hidden";
  id?: string;
  className?: string;
}

export function Text(props: Props): React.ReactNode {
  const classNames = classnames(
    styles[`textSize-${props.size || "standard"}`],
    styles[`textWeight-${props.weight || "regular"}`],
    props.transform && styles[`transform-${props.transform}`],
    props.truncate && styles.truncate,
    props.clamped && styles.clamped,
    props.clamped && styles[`lines-${props.clamped}`],
    props.color && styles[`color-${props.color}`],
    props.lineThrough && styles.lineThrough,
    props.font && styles[`font-${props.font}`],
    props.priority && styles[`priority-${props.priority}`],
    props.lineHeight && styles[`lineHeight-${props.lineHeight}`],
    props.whiteSpace && styles[`whiteSpace-${props.whiteSpace}`],
    props.wordWrap && styles[`wordWrap-${props.wordWrap}`],
    props.visibility === "hidden" && styles["visibility-hidden"],
    props.className,
  );

  return (
    <Box
      textAlign={props.align}
      className={classNames}
      component={props.inline ? "span" : undefined}
      id={props.id}
    >
      {props.children}
    </Box>
  );
}
